/**
 * BrioTabs -- keyboard navigation, focus management, and panel
 * switching for ARIA-roled tab groups.
 *
 * Wraps a tablist + tabpanels structure. The browser fires
 * connectedCallback when <brio-tabs> enters the DOM and
 * disconnectedCallback when it leaves, so setup and teardown happen
 * automatically with the element's lifecycle.
 *
 * What this element adds (beyond the platform's defaults):
 *
 *   1. Roving tabindex across [role="tab"] children. Only the active
 *      tab is in the page tab order; arrow keys / Home / End walk
 *      between siblings within the tablist. Provided by
 *      BrioBase.keyNav, the same helper brio-dropdown uses.
 *
 *   2. Activation -- two modes per the WAI-ARIA APG:
 *
 *      Automatic (default): arrow keys focus AND activate the next
 *      tab in one keypress. The user sees panel content change
 *      immediately as they navigate. Standard convention; matches
 *      what users expect when panels switch instantly.
 *
 *      Manual (`<brio-tabs manual>`): arrow keys focus only; the
 *      user presses Enter or Space to activate the focused tab.
 *      Useful when activation has side effects (network requests,
 *      heavy compute, instrumentation events) that shouldn't fire
 *      on every arrow press. Implemented by simply not running the
 *      activation callback on focus -- Enter / Space activate via
 *      the native button click event, which already bubbles to the
 *      click delegation handler below.
 *
 *   3. Click delegation on the tablist -- one listener handles every
 *      tab click. Disabled tabs (:disabled or [aria-disabled="true"])
 *      are filtered out so they can't be activated by mouse.
 *
 *   4. aria-orientation="vertical" on the tablist when the host has
 *      class="is-vertical" -- screen readers use this to announce
 *      the orientation and to switch their own arrow-key expectations.
 *
 *   5. brio:tab-change CustomEvent on every activation. Bubbles.
 *      Detail payload: { tab, previous, panel, panelId }. Consumers
 *      (datastar, htmx, alpine, plain JS) can listen for the event
 *      to fetch panel content, instrument analytics, etc.
 *
 * Expected markup:
 *   <brio-tabs class="tabs">
 *       <div role="tablist">
 *           <button class="tab" type="button" role="tab"
 *                   aria-selected="true" aria-controls="p1" id="t1">One</button>
 *           <button class="tab" type="button" role="tab"
 *                   aria-controls="p2" id="t2" tabindex="-1">Two</button>
 *       </div>
 *       <div class="tab-panel" role="tabpanel" id="p1" aria-labelledby="t1">...</div>
 *       <div class="tab-panel" role="tabpanel" id="p2" aria-labelledby="t2" hidden>...</div>
 *   </brio-tabs>
 *
 * Class / attribute split:
 *   - Visual variants are CSS classes on .tabs (.is-vertical, .is-pills,
 *     .is-stretched). The JS reads .is-vertical to swap arrow-key
 *     direction.
 *   - Behavioral toggle (manual activation) is an attribute on the
 *     custom element itself: <brio-tabs manual>. Same pattern as
 *     <brio-dialog dismissible>. CSS doesn't care; JS reads it.
 */

import { BrioBase } from './brio-base.js';

class BrioTabs extends BrioBase {
    static tag = 'brio-tabs';
    static {
        customElements.define(BrioTabs.tag, BrioTabs);
    }

    // Private class fields. Stored on the instance so disconnectedCallback
    // can remove the exact function references it added.
    #tablist;
    #cleanupNav;
    #onClick;
    #lastActive;

    connectedCallback() {
        this.#tablist = this.$('[role="tablist"]');
        if (!this.#tablist) return;

        // Track the initial active tab so a click on the already-selected
        // tab doesn't fire a redundant tab-change event.
        this.#lastActive = this.$('[role="tab"][aria-selected="true"]');

        const vertical = this.classList.contains('is-vertical');
        if (vertical) {
            this.#tablist.setAttribute('aria-orientation', 'vertical');
        }

        const manual = this.hasAttribute('manual');

        // Roving tabindex + arrow / Home / End handling. Selector skips
        // disabled tabs in either form (:disabled on <button>,
        // [aria-disabled="true"] anywhere) so keyboard nav matches what
        // mouse interaction does.
        //
        // Activation strategy:
        //   - Automatic: pass an onFocus that activates as focus moves.
        //   - Manual: omit onFocus. Arrow keys move focus; the user
        //     activates with Enter/Space, which the browser turns into
        //     a native click event on the focused <button>. That click
        //     bubbles to the tablist's click handler below, which runs
        //     activation. So manual mode needs no separate keydown
        //     listener -- the platform already wires Enter/Space.
        const ITEM_SELECTOR = '[role="tab"]:not(:disabled):not([aria-disabled="true"])';
        this.#cleanupNav = this.keyNav(ITEM_SELECTOR, {
            vertical,
            onFocus: manual ? null : (tab) => this.#activate(tab),
        });

        // Click delegation -- one listener on the tablist, finds the
        // [role="tab"] ancestor of the click target via closest() so
        // clicks on a tab's children (icon span, badge, etc.) still
        // resolve to the tab. Listener on the tablist (not on this)
        // means clicks anywhere inside a tab panel don't trigger
        // activation logic.
        this.#onClick = (e) => {
            const tab = e.target.closest('[role="tab"]');
            if (!tab) return;
            if (tab.matches(':disabled, [aria-disabled="true"]')) return;
            this.#activate(tab);
        };
        this.#tablist.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
        this.#cleanupNav?.();
        if (this.#tablist && this.#onClick) {
            this.#tablist.removeEventListener('click', this.#onClick);
        }
    }

    /**
     * Activate a tab: update ARIA state on every tab, manage roving
     * tabindex, show the matching panel, hide the others, and emit
     * the tab-change event.
     *
     * Roving tabindex pattern: only the active tab is in the document
     * tab order. We achieve that by removing the tabindex attribute on
     * the active tab (a <button> with no tabindex defaults to 0 and
     * is in the tab order) and setting tabindex="-1" on every other
     * tab (still focusable programmatically, including via arrow
     * keys, but skipped by Tab key navigation).
     *
     * Skips re-activation if the tab is already active. That keeps the
     * tab-change event from firing on no-op clicks (clicking the
     * already-selected tab) -- consumers using the event for SSE
     * fetches won't get duplicate requests.
     */
    #activate(tab) {
        if (tab === this.#lastActive) return;

        const previous = this.#lastActive;

        for (const t of this.$$('[role="tab"]')) {
            t.setAttribute('aria-selected', 'false');
            t.setAttribute('tabindex', '-1');
        }
        tab.setAttribute('aria-selected', 'true');
        tab.removeAttribute('tabindex');

        const panelId = tab.getAttribute('aria-controls');
        let panel = null;
        for (const p of this.$$('[role="tabpanel"]')) {
            const isActive = p.id === panelId;
            p.hidden = !isActive;
            if (isActive) panel = p;
        }

        this.#lastActive = tab;
        this.emit('tab-change', { tab, previous, panel, panelId });
    }
}
